import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Registration = {
  onActivate: () => void;
};

type ContextValue = {
  register: (target: HTMLElement, onActivate: () => void) => () => void;
};

const ScrollActivateContext = createContext<ContextValue | null>(null);

type ProviderProps = {
  rootEl: HTMLElement | null;
  threshold?: number;
  debounceMs?: number;
  children: ReactNode;
};

/**
 * Tracks visibility ratios for registered targets within `rootEl` and, when
 * scrolling settles, activates the most-visible target whose ratio is at or
 * above `threshold`. Settling is detected via the `scrollend` event when
 * available, with a `debounceMs` timeout as a fallback for older browsers.
 * Activations only happen in response to scroll events — not on mount or
 * layout changes — so selecting a target via click does not trigger a
 * re-activation cascade.
 */
export const ScrollActivateProvider = ({
  rootEl,
  threshold = 0.7,
  debounceMs = 250,
  children,
}: ProviderProps) => {
  const ratiosRef = useRef(new Map<HTMLElement, number>());
  const registrationsRef = useRef(new Map<HTMLElement, Registration>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!rootEl) return;

    const thresholds = Array.from({ length: 21 }, (_, i) => i / 20);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratiosRef.current.set(
            entry.target as HTMLElement,
            entry.intersectionRatio
          );
        }
      },
      { root: rootEl, threshold: thresholds }
    );
    observerRef.current = observer;

    registrationsRef.current.forEach((_, target) => observer.observe(target));

    const clearScrollTimer = () => {
      if (scrollTimerRef.current !== null) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    };

    const activate = () => {
      let bestTarget: HTMLElement | null = null;
      let bestRatio = threshold;
      for (const [target, ratio] of ratiosRef.current) {
        if (ratio >= bestRatio && registrationsRef.current.has(target)) {
          bestTarget = target;
          bestRatio = ratio;
        }
      }
      if (bestTarget) {
        registrationsRef.current.get(bestTarget)?.onActivate();
      }
    };

    const handleScroll = () => {
      clearScrollTimer();
      scrollTimerRef.current = setTimeout(() => {
        scrollTimerRef.current = null;
        activate();
      }, debounceMs);
    };

    const handleScrollEnd = () => {
      clearScrollTimer();
      activate();
    };

    rootEl.addEventListener("scroll", handleScroll, { passive: true });
    rootEl.addEventListener("scrollend", handleScrollEnd, { passive: true });

    return () => {
      observer.disconnect();
      observerRef.current = null;
      rootEl.removeEventListener("scroll", handleScroll);
      rootEl.removeEventListener("scrollend", handleScrollEnd);
      clearScrollTimer();
    };
  }, [rootEl, threshold, debounceMs]);

  const register = useCallback(
    (target: HTMLElement, onActivate: () => void) => {
      registrationsRef.current.set(target, { onActivate });
      observerRef.current?.observe(target);
      return () => {
        registrationsRef.current.delete(target);
        ratiosRef.current.delete(target);
        observerRef.current?.unobserve(target);
      };
    },
    []
  );

  const value = useMemo(() => ({ register }), [register]);

  return (
    <ScrollActivateContext.Provider value={value}>
      {children}
    </ScrollActivateContext.Provider>
  );
};

export const useScrollActivate = (onActivate: () => void, enabled = true) => {
  const context = useContext(ScrollActivateContext);
  const onActivateRef = useRef(onActivate);

  useEffect(() => {
    onActivateRef.current = onActivate;
  }, [onActivate]);

  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!target || !context || !enabled) return;
    return context.register(target, () => onActivateRef.current());
  }, [target, context, enabled]);

  return setTarget;
};
